import { Suspense, useCallback, useState } from 'react';
import { FiltersButton } from 'common/components/Filters/FiltersButton';

import { useAtom, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import Box from '@mui/material/Box';
import {
  TabBar,
  TabBarActions,
  TabBarChips,
  TabBarFilters,
} from 'common/components/AppLayout/TabBar/TabBar';
import Grid from '@mui/material/Grid';
import { RefetchQueryButton } from 'common/components/Queries/RefetchQueryButton';
import { useHasActiveFilters } from './hooks/useHasActiveFilters';
import {
  chosenIdTypeFilterAtom,
  searchTextFilterAtom,
  iccidRangeFilterAtom,
  simProductsFiltersAtom,
} from 'atoms/filters/sim-products';
import { syncHintsAtom, updateHintsAtom } from 'atoms/hints/sim-products';
import { SimProductsFiltersDialogAtomInitializer } from './Atoms/SimFiltersDialogAtomInitializator';
import { SimProductsFiltersDialog } from './SimProductsFiltersDialog';
import { SimProductsFilterChips } from './Chips/SimProductsFilterChips';
import { ProductExportButton } from '../Reports/ProductExportButton';
import { removeNullValuesFromHintsObject } from 'src/common/utils/removeNullValuesFromHintsObject';
import { simProductsQueries } from '../Inventory/queries/simProductsQueries';
import { useSimDebouncedIdSearch } from 'common/components/Sim/Filters/useSimDebouncedIdSearch';
import { SimIdSearchInput } from 'src/common/components/Sim/Filters/SimIdSearchInput';
import { normalizeCustomFields } from 'src/common/components/Filters/Inputs/customFields/untils/normalizeCustomFields';
import { SimProductActionToolbar } from '../Actions/SimProductActionToolbar';
import { useIsSimInLifeChangesAvailable } from 'products/sim/Actions/hooks/useIsSimInLifeChangesAvailable.ts';

export const SimProductsFilters = () => {
  const [open, setOpen] = useState(false);

  const setFilters = useSetAtom(simProductsFiltersAtom);
  const resetFilters = useResetAtom(simProductsFiltersAtom);

  const setHints = useSetAtom(updateHintsAtom);
  const syncHints = useSetAtom(syncHintsAtom);

  const onApply = useCallback((filters: any) => {
    setFilters(filters);

    const normalizedFilters = {
      ...filters,
      customFields: normalizeCustomFields(filters.customFields),
    };
    setHints(removeNullValuesFromHintsObject(normalizedFilters));
    setOpen(false);
  }, []);

  const onClose = useCallback(() => {
    syncHints();
    setOpen(false);
  }, []);

  const hasChips = useHasActiveFilters();

  const [chosenIdType, setChosenIdType] = useAtom(chosenIdTypeFilterAtom);
  const { inputValue, onChange, stopDebouncing } = useSimDebouncedIdSearch({
    iccidRangeFilterAtom,
    searchTextFilterAtom,
  });

  const inlifeChangesEnabled = useIsSimInLifeChangesAvailable();

  return (
    <TabBar>
      <Grid container rowGap={4} sx={{ mt: 0, width: '100%' }} justifyContent={'space-between'}>
        <TabBarFilters>
          <Box sx={{ flex: 1, maxWidth: '22.5rem' }}>
            <SimIdSearchInput
              chosenId={chosenIdType}
              onChosenIdChange={setChosenIdType}
              search={inputValue}
              onSearchChange={onChange}
            />
          </Box>
          <Suspense>
            <div data-testid="sims products search menu">
              <FiltersButton
                onClick={() => {
                  stopDebouncing();
                  setOpen(true);
                }}
                active={false}
              />
              {open && (
                <SimProductsFiltersDialogAtomInitializer>
                  <SimProductsFiltersDialog
                    onApply={onApply}
                    onCancel={onClose}
                    onClear={resetFilters}
                    onClose={onClose}
                  />
                </SimProductsFiltersDialogAtomInitializer>
              )}
            </div>
          </Suspense>
          {inlifeChangesEnabled && <SimProductActionToolbar />}
        </TabBarFilters>
        <TabBarActions>
          <RefetchQueryButton queryKey={simProductsQueries.all} />
          <ProductExportButton />
        </TabBarActions>
      </Grid>
      <TabBarChips>{hasChips ? <SimProductsFilterChips /> : null}</TabBarChips>
    </TabBar>
  );
};
